import { FormFieldTypes } from "types/enums.ts";
import { z } from "zod";

import { TenantRegistrationFormData } from "./types.ts";

import FormFieldData = App.Data.FormFieldData;

const isBlank = (value: unknown) =>
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0);

export const buildRegistrationFormSchema = (fields: FormFieldData[]): z.ZodType<TenantRegistrationFormData> =>
    z
        .object({
            email: z
                .string()
                .min(1, "forms.general-validation.email_required")
                .email("forms.general-validation.email_invalid"),
            nickname: z.string().min(1, "forms.general-validation.nickname_required"),
            fields: z.record(z.unknown()),
        })
        .superRefine((data, context) => {
            fields.forEach((field) => {
                if (field.is_hidden) return;

                const value = data.fields[field.system_name];
                if (
                    field.is_required &&
                    (isBlank(value) || (field.type === FormFieldTypes.Checkbox && value !== true))
                ) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "forms.general-validation.required",
                        path: ["fields", field.system_name],
                    });
                    return;
                }

                if (isBlank(value)) return;

                if (field.type === FormFieldTypes.Number && typeof value === "number") {
                    if (field.min !== null && value < field.min) {
                        context.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "forms.registration-fields.validation.minimum",
                            path: ["fields", field.system_name],
                        });
                    }
                    if (field.max !== null && value > field.max) {
                        context.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "forms.registration-fields.validation.maximum",
                            path: ["fields", field.system_name],
                        });
                    }
                }
            });
        });

const parseBooleanDefault = (value: string | null): boolean =>
    value === "1" || value?.toLowerCase() === "true" || value?.toLowerCase() === "on";

const parseArrayDefault = (value: string | null): string[] => {
    if (!value) return [];

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
        return [];
    }
};

const getDefaultValue = (field: FormFieldData): unknown => {
    switch (field.type) {
        case FormFieldTypes.Checkbox:
            return parseBooleanDefault(field.default_value);
        case FormFieldTypes.MultiSelect:
        case FormFieldTypes.MultiRadio:
            return parseArrayDefault(field.default_value);
        case FormFieldTypes.Number:
            return field.default_value === null || field.default_value === "" ? null : Number(field.default_value);
        case FormFieldTypes.Country:
        case FormFieldTypes.Currency:
            return field.default_value && /^\d+$/.test(field.default_value)
                ? Number(field.default_value)
                : field.default_value;
        default:
            return field.default_value ?? "";
    }
};

export const buildRegistrationFormDefaults = (fields: FormFieldData[]): TenantRegistrationFormData => ({
    email: "",
    nickname: "",
    fields: Object.fromEntries(fields.map((field) => [field.system_name, getDefaultValue(field)])),
});
